import { CategoryType } from "@/app/generated/prisma"
import { TOPIC_CATEGORY_SLUGS } from "@/lib/navigation-links"
import { Routes } from "@/lib/routes"
import { generateSlug } from "@/utils/helpers/utils"

export function isTopicCategory(category: string | null | undefined) {
   if (!category) return false
   return TOPIC_CATEGORY_SLUGS.has(generateSlug(category))
}

// The category an article should actually be presented under.
//
// A soft-deleted Category is no longer a live section, so its articles fall back
// to news instead of linking at a category page that is gone. Note that the
// soft-delete Prisma extension only covers `article`, not `category`, so a
// deleted topic still comes back from a query - it has to be checked here.
//
// Callers must select `topic.isDeleted` for the fallback to apply; where it is
// absent this behaves exactly as the previous `topic?.name ?? category`.
export const CATEGORY_FALLBACK = "news"

type ArticleCategoryShape = {
   category?: string | null
   topic?: { name?: string | null; isDeleted?: boolean | null; categoryType?: CategoryType | null } | null
}

export function resolveArticleCategory(article: ArticleCategoryShape): string {
   if (article.topic?.isDeleted) return CATEGORY_FALLBACK
   return article.topic?.name ?? article.category ?? CATEGORY_FALLBACK
}

// Whether an article belongs on a Blog ("topic") page rather than a News page.
//
// `topic.categoryType` is the reliable source of truth when present: unlike a plain
// name match, it can't be fooled by a News category that happens to share its name
// with an unrelated Blog nav topic (e.g. a News category literally named
// "operations" colliding with the Blog nav's "Operations" topic, which used to send
// genuine News articles to /blog/). Categories that predate `categoryType` being
// backfilled — or articles with no `topic` relation at all — fall back to the
// legacy name-based `isTopicCategory` check, same behavior as before this existed.
export function isTopicArticle(article: ArticleCategoryShape): boolean {
   if (article.topic?.isDeleted) return false // falls back to news, see resolveArticleCategory
   if (article.topic?.categoryType) return article.topic.categoryType === CategoryType.Blogs
   return isTopicCategory(resolveArticleCategory(article))
}

export function getArticlePath(article: ArticleCategoryShape & { slug: string }) {
   return isTopicArticle(article)
      ? Routes.client.topic.getPath({ slug: article.slug })
      : Routes.client.news.getPath({ slug: article.slug })
}

export function getCategoryPath(article: ArticleCategoryShape | null | undefined) {
   if (!article) return Routes.client.newsPage.getPath()
   const slug = generateSlug(resolveArticleCategory(article)) || "news"
   return isTopicArticle(article) ? Routes.client.topic.getPath({ slug }) : Routes.client.news.getPath({ slug })
}

export function getPostNavSection(article: ArticleCategoryShape | null | undefined): "/topics/" | "/news/" {
   if (!article) return "/news/"
   return isTopicArticle(article) ? "/topics/" : "/news/"
}
